import { useState, useRef, useEffect } from 'react';
import { Sparkles, Search, Plus, Upload, ChevronDown, X, ScrollText, Library } from 'lucide-react';
import { getTagColor } from './utils/tagColors';
import { useNavigate, Link } from '@tanstack/react-router';
import { useChatStore } from '../../../components/Pleri/store/chatStore';
import { useSkillManager } from './hooks/useSkillManager';
import { SkillCard } from './components/SkillCard';
import { SkillsShell } from './components/SkillsShell';
import { TemplateGallery } from './components/TemplateGallery';
import { ImportDialog } from './components/ImportDialog';
import { encodeSkillRef } from './utils/skillRef';
import type { ScopeFilter } from './types';
import type { TFunction } from 'i18next';

import { useTranslation } from 'react-i18next';

const exampleSkills = (t: TFunction<'pleri/skills'>) => [
  {
    name: t('incidentResponseRunbook'),
    tag: t('common:security'),
    tagClass: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    description: t('structuredIncidentResponseWorkflowFollowingNist'),
  },
  {
    name: t('complianceAudit'),
    tag: t('compliance'),
    tagClass: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    description: t('reviewCompliancePostureAcrossSoc2Iso'),
  },
  {
    name: t('weeklySecurityDigest'),
    tag: t('reporting'),
    tagClass: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    description: t('generateStructuredWeeklySecurityDigestsWith'),
  },
];

const scopeTabs = (
  t: TFunction<'pleri/skills'>,
): { value: ScopeFilter; label: string }[] => [
  { value: 'all', label: t('all') },
  { value: 'my', label: t('mine') },
  { value: 'tenant', label: t('common:tenant') },
  { value: 'template', label: t('templates') },
  { value: 'pleri', label: t('pleri') },
];

function CardGridSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800"
        >
          <div className="h-0.5 w-full animate-pulse bg-gray-200 dark:bg-gray-700" />
          <div className="p-4">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
                <div className="h-3 w-1/2 animate-pulse rounded-full bg-gray-200 dark:bg-gray-700" />
              </div>
            </div>
            <div className="mt-3 space-y-1.5">
              <div className="h-3 w-full animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
              <div className="h-3 w-5/6 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function NewSkillButton({ onNew, onNewWithChat }: { onNew: () => void; onNewWithChat: () => void }) {
  const { t } = useTranslation('pleri/skills');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-lg bg-orange-500 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-600 active:bg-orange-700"
      >
        <Plus className="h-4 w-4" />
        {t('new')}
        <ChevronDown className="h-3.5 w-3.5 opacity-70" />
      </button>
      {open && (
        <div className="absolute top-full right-0 z-20 mt-1.5 w-52 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800">
          <button
            type="button"
            className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700"
            onClick={() => { setOpen(false); onNew(); }}
          >
            <Plus className="h-4 w-4 text-gray-400" />
            <span>{t('newSkill')}</span>
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left text-sm hover:bg-purple-50 dark:hover:bg-purple-900/20"
            onClick={() => { setOpen(false); onNewWithChat(); }}
          >
            <Sparkles className="h-4 w-4 text-purple-500" />
            <span className="text-purple-600 dark:text-purple-400">
              {t('buildWithPleri')}
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

export function PleriSkillsPage() {
  const { t } = useTranslation('pleri/skills');
  const navigate = useNavigate();
  const manager = useSkillManager();
  const {
    skills,
    isLoading,
    showTemplateGallery,
    showImportDialog,
    disabledSkillIds,
    scopeFilter,
    tagFilter,
    tagNames,
    visibleSkills,
    searchQuery,
    isAdmin,
    setShowTemplateGallery,
    setShowImportDialog,
    setScopeFilter,
    setTagFilter,
    setSearchQuery,
    getDisableId,
  } = manager;

  const handleCreateWithChat = () => {
    const store = useChatStore.getState();
    const conversationId = store.createConversation();
    store.setInputPrompt(`Hey Pleri help me create a new skill for you`);
    store.submitInputPrompt();
    navigate({ to: '/c/$conversationId', params: { conversationId } });
  };

  const personalSkills = skills?.filter((s) => s.scope === 'user' || s.scope === 'tenant') ?? [];
  const hasNoPersonalSkills = !isLoading && skills !== undefined && personalSkills.length === 0;

  const visibleTags = tagNames.filter((g) => visibleSkills.some((s) => s.tags?.includes(g)));

  return (
    <div className="flex h-full w-full flex-col bg-gray-50 dark:bg-gray-950">
      <TemplateGallery
        isOpen={showTemplateGallery}
        onClose={() => setShowTemplateGallery(false)}
        onInstalled={(skillId) => {
          setShowTemplateGallery(false);
          const [scope, ...nameParts] = skillId.split(':');
          const name = nameParts.join(':');
          navigate({ to: '/skills/$skillRef', params: { skillRef: encodeSkillRef(scope, name) } });
        }}
      />
      <ImportDialog
        isOpen={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onImported={(skillId) => {
          setShowImportDialog(false);
          const [scope, ...nameParts] = skillId.split(':');
          const name = nameParts.join(':');
          navigate({ to: '/skills/$skillRef', params: { skillRef: encodeSkillRef(scope, name) } });
        }}
        isAdmin={isAdmin}
      />
      <SkillsShell
        activeTab="my-skills"
        mySkillsCount={personalSkills.length}
        actions={
          <>
            <button
              type="button"
              onClick={() => setShowImportDialog(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
            >
              <Upload className="h-3.5 w-3.5" />
              {t('import')}
            </button>
            <NewSkillButton
              onNew={() => navigate({ to: '/skills/new' })}
              onNewWithChat={handleCreateWithChat}
            />
          </>
        }
      >
        {/* Search + scope filter row */}
        <div className="flex items-center gap-3 border-t border-gray-100 px-6 py-3 dark:border-gray-800">
          <div className="relative max-w-xs flex-1">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t('searchSkills')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 py-1.5 pr-8 pl-8 text-sm placeholder-gray-400 focus:border-orange-400 focus:bg-white focus:ring-1 focus:ring-orange-400 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute top-1/2 right-2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-0.5">
            {scopeTabs(t).map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => { setScopeFilter(tab.value); setTagFilter([]); }}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                  scopeFilter === tab.value
                    ? 'bg-orange-500 text-white'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tag filter chips */}
        {visibleTags.length > 0 && (
          <div className="flex items-center gap-1.5 overflow-x-auto border-t border-gray-100 px-6 py-2 dark:border-gray-800">
            <span className="flex-shrink-0 text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
              {t('tags')}
            </span>
            <button
              type="button"
              onClick={() => setTagFilter([])}
              className={`flex-shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors ${
                tagFilter.length === 0
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400'
              }`}
            >
              {t('all')}
            </button>
            {visibleTags.map((g) => {
              const c = getTagColor(g);
              return (
                <button
                  key={g}
                  type="button"
                  onClick={() =>
                    setTagFilter(tagFilter.includes(g) ? tagFilter.filter((t) => t !== g) : [...tagFilter, g])
                  }
                  className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
                    tagFilter.includes(g)
                      ? `${c.filter} font-semibold`
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400'
                  }`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />
                  {g}
                </button>
              );
            })}
          </div>
        )}
      </SkillsShell>
      {/* Card grid */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        {isLoading && !skills ? (
          <CardGridSkeleton />
        ) : hasNoPersonalSkills ? (
          <div className="flex flex-col items-center py-10">
            {/* Faded example cards */}
            <p className="mb-3 text-xs font-medium uppercase tracking-wide text-gray-300 dark:text-gray-700">
              {t('exampleSkills')}
            </p>
            <div className="pointer-events-none mb-10 grid w-full select-none grid-cols-1 gap-3 opacity-40 blur-[1.5px] sm:grid-cols-3">
              {exampleSkills(t).map((ex) => (
                <div
                  key={ex.name}
                  className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-orange-100 dark:bg-orange-900/30">
                      <ScrollText className="h-4 w-4 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{ex.name}</p>
                      <span className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-xs font-semibold uppercase tracking-wide ${ex.tagClass}`}>
                        {ex.tag}
                      </span>
                    </div>
                  </div>
                  <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                    {ex.description}
                  </p>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/30">
              <ScrollText className="h-5 w-5 text-orange-500" />
            </div>
            <h2 className="mt-4 text-base font-semibold text-gray-900 dark:text-gray-100">
              {t('noSkillsYet')}
            </h2>
            <p className="mt-1.5 max-w-sm text-center text-xs text-gray-400 dark:text-gray-500">
              {t('createASkillToGivePleriSpecialized')}
            </p>
            <div className="mt-5 flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate({ to: '/skills/new' })}
                className="inline-flex items-center gap-1.5 rounded-lg bg-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-600 active:bg-orange-700"
              >
                <Plus className="h-4 w-4" />
                {t('createASkill')}
              </button>
              <Link
                to="/skills/library"
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
              >
                <Library className="h-4 w-4" />
                {t('browseLibrary')}
              </Link>
            </div>
          </div>
        ) : (
          <>
            {skills && visibleSkills.length > 0 && (
              <p className="mb-3 text-xs text-gray-400 dark:text-gray-500">
                {t('showingOfSkills', {
                  shown: visibleSkills.length,
                  count: skills.length,
                })}
              </p>
            )}
            {visibleSkills.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <Sparkles className="mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {searchQuery
                    ? t('noSkillsMatchYourSearch')
                    : t('noSkillsHereYet')}
                </p>
                {!searchQuery && (
                  <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                    {t('createANewSkillOrBrowseThe')}
                  </p>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {visibleSkills.map((skill) => (
                  <Link
                    key={manager.getSkillId(skill)}
                    to="/skills/$skillRef"
                    params={{ skillRef: encodeSkillRef(skill.scope, skill.name) }}
                    className="block h-full"
                  >
                    <SkillCard
                      skill={skill}
                      isDisabledByUser={disabledSkillIds.includes(getDisableId(skill))}
                      currentUserId={manager.currentUserId ?? undefined}
                      onClick={() => {}}
                    />
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
